/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { createContext, useContext, useState, useEffect } from "react";

const UIContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [lang, setLang] = useState(localStorage.getItem("lang") || "ar");

  // Apply theme and language on mount and changes
  useEffect(() => {
    // Apply theme class
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    // Apply language direction
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [theme, lang]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const toggleLang = () => {
    const newLang = lang === "ar" ? "en" : "ar";
    setLang(newLang);
    localStorage.setItem("lang", newLang);
  };

  // Color constants - Matching admindashboard design
  const colors = {
    // Main colors
    primary: theme === "dark" ? "#FFD700" : "#6A11CB",
    gold: "#FFD700",
    green: "#22C55E",
    red: "#EF4444",
    blue: "#2563EB",

    // Background colors
    cardBg: theme === "dark" ? "#1e293b" : "#ffffff",
    bg: theme === "dark" ? "#0f172a" : "#f8fafc",

    // Border and divider colors
    border: theme === "dark" ? "#334155" : "#e2e8f0",
    dividerColor: theme === "dark" ? "#475569" : "#e5e7eb",

    // Text colors
    text: theme === "dark" ? "#f1f5f9" : "#1e293b",
    textColor: theme === "dark" ? "#f1f5f9" : "#1e293b", // Alias for compatibility
    subText: theme === "dark" ? "#94a3b8" : "#64748b",

    // Main accent color (Purple for light, Gold for dark) - matching admindashboard
    mainColor: theme === "dark" ? "#FFD700" : "#6A11CB",
    mainColorHover: theme === "dark" ? "#ffed4e" : "#5b0fb3",
    mainColorLight: theme === "dark" ? "#fef08a" : "#8b5cf6",

    // Table colors
    tableHeaderBg: theme === "dark" ? "#1e293b" : "#f8fafc",
    tableRowHover: theme === "dark" ? "#334155" : "#f3e8ff",
    hoverBg: theme === "dark" ? "#334155" : "#f3e8ff", // For table header hover

    // Input colors
    inputBg: theme === "dark" ? "#1e293b" : "#ffffff",
    inputBorder: theme === "dark" ? "#475569" : "#d1d5db",
    inputFocus: theme === "dark" ? "#FFD700" : "#6A11CB",

    // Border colors (aliases for compatibility)
    borderColor: theme === "dark" ? "#334155" : "#e2e8f0", // Alias for border

    // Badge colors
    badgeSuccess: theme === "dark" ? "#22c55e" : "#dcfce7",
    badgeError: theme === "dark" ? "#ef4444" : "#fee2e2",
    badgeWarning: theme === "dark" ? "#f59e0b" : "#fef3c7",
    badgeInfo: theme === "dark" ? "#3b82f6" : "#dbeafe",
    badgeSuccessText: theme === "dark" ? "#ffffff" : "#166534",
    badgeErrorText: theme === "dark" ? "#ffffff" : "#991b1b",
  };

  return (
    <UIContext.Provider
      value={{
        theme,
        lang,
        toggleTheme,
        toggleLang,
        isDark: theme === "dark",
        isArabic: lang === "ar",
        colors,
      }}
    >
      {children}
    </UIContext.Provider>
  );
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context)
    throw new Error("useUI must be used inside a ThemeProvider");
  return context;
}
